'use client';

import { useState } from 'react';
import SearchBar from './SearchBar';
import ConditionCard from './ConditionCard';
import Icon from '@/components/ui/AppIcon';

interface RatingCriteria {
  rating: number;
  requirements: string;
  examples?: string;
  monthlyCompensation: number;
}

interface Condition {
  id: string;
  name: string;
  description: string;
  plainLanguageExplanation: string;
  category: string;
  maxRating: number;
  commonSymptoms: string[];
  ratingCriteria: RatingCriteria[];
  evidenceRequired: string[];
  diagnosticCode: string;
  alt: string;
}

const conditions: Condition[] = [
  {
    id: '1',
    name: 'Post-Traumatic Stress Disorder (PTSD)',
    description: 'Mental health condition triggered by experiencing or witnessing traumatic events during military service.',
    plainLanguageExplanation: 'PTSD is a mental health condition that can develop after experiencing combat, military sexual trauma, or other traumatic events during service. It affects how you think, feel, and react to everyday situations.',
    category: 'Mental Health',
    maxRating: 100,
    commonSymptoms: [
      'Recurring nightmares or flashbacks of traumatic events',
      'Severe anxiety, panic attacks, or constant feeling of being on edge',
      'Difficulty sleeping or concentrating',
      'Avoiding people, places, or activities that remind you of trauma',
      'Feeling emotionally numb or detached from loved ones',
      'Angry outbursts or aggressive behavior'
    ],
    ratingCriteria: [
      {
        rating: 10,
        requirements: 'Mild symptoms that decrease work efficiency only during periods of high stress',
        examples: 'Occasional nightmares, mild anxiety in crowded places, able to maintain employment',
        monthlyCompensation: 171
      },
      {
        rating: 30,
        requirements: 'Symptoms causing some impairment in work, social functioning, but generally able to function',
        examples: 'Regular panic attacks, difficulty maintaining relationships, reduced work performance',
        monthlyCompensation: 524
      },
      {
        rating: 50,
        requirements: 'Serious symptoms causing significant impairment in most areas of life',
        examples: 'Severe panic attacks, difficulty leaving home, problems keeping a job, strained relationships',
        monthlyCompensation: 1075
      },
      {
        rating: 70,
        requirements: 'Major impairment in most life areas including work, family, judgment, and mood',
        examples: 'Cannot maintain employment, isolated from family/friends, suicidal thoughts, substance abuse',
        monthlyCompensation: 1716
      },
      {
        rating: 100,
        requirements: 'Total occupational and social impairment requiring continuous supervision',
        examples: 'Cannot care for self, constant suicidal ideation, severe memory loss, inability to function',
        monthlyCompensation: 3737
      }
    ],
    evidenceRequired: [
      'VA mental health examination report',
      'Service treatment records showing in-service stressor event',
      'Buddy statements describing observed behavior changes',
      'Private treatment records (therapist, psychiatrist)',
      'Stressor statement detailing traumatic events',
      'Employment records showing job performance issues'
    ],
    diagnosticCode: '9411',
    alt: 'PTSD mental health condition information card'
  },
  {
    id: '2',
    name: 'Tinnitus (Ringing in Ears)',
    description: 'Constant or recurring ringing, buzzing, or other sounds in the ears not caused by external noise.',
    plainLanguageExplanation: 'Tinnitus is when you hear ringing, buzzing, hissing, or other sounds in your ears that aren\'t coming from outside. It\'s often caused by exposure to loud noises during military service like gunfire, aircraft, or explosions.',
    category: 'Hearing',
    maxRating: 10,
    commonSymptoms: [
      'Constant ringing, buzzing, or hissing in one or both ears',
      'Sounds may get louder in quiet environments',
      'Difficulty concentrating or sleeping due to the noise',
      'Increased stress or anxiety from constant sound',
      'Sounds may worsen with stress or fatigue'
    ],
    ratingCriteria: [
      {
        rating: 10,
        requirements: 'Recurrent tinnitus confirmed by audiological examination',
        examples: 'Any level of tinnitus, from mild occasional ringing to severe constant noise - VA only has one rating',
        monthlyCompensation: 171
      }
    ],
    evidenceRequired: [
      'Audiological examination from VA or private audiologist',
      'Statement describing when tinnitus started and how it affects daily life',
      'Service records showing noise exposure (weapons qualification, aircraft crew, etc.)',
      'Buddy statements from fellow service members about noise exposure',
      'Any hearing aids or treatment records'
    ],
    diagnosticCode: '6260',
    alt: 'Tinnitus hearing condition information card'
  },
  {
    id: '3',
    name: 'Migraine Headaches',
    description: 'Severe recurring headaches often accompanied by nausea, sensitivity to light/sound, and visual disturbances.',
    plainLanguageExplanation: 'Migraines are intense headaches that can be disabling, often causing severe pain on one side of your head along with nausea, vomiting, and sensitivity to light and sound. They can last hours or days and make it impossible to work or function normally.',
    category: 'Neurological',
    maxRating: 50,
    commonSymptoms: [
      'Severe throbbing pain, usually on one side of head',
      'Nausea and vomiting',
      'Extreme sensitivity to light, sound, or smells',
      'Visual disturbances (auras) before headache starts',
      'Dizziness or feeling lightheaded',
      'Need to rest in dark, quiet room during episodes'
    ],
    ratingCriteria: [
      {
        rating: 0,
        requirements: 'Headaches documented but not severe enough to be prostrating',
        examples: 'Mild headaches that don\'t interfere with work or daily activities',
        monthlyCompensation: 0
      },
      {
        rating: 10,
        requirements: 'With less frequent attacks producing mild economic inadaptability',
        examples: '1-2 migraines per month that may require rest but don\'t completely disable you',
        monthlyCompensation: 171
      },
      {
        rating: 30,
        requirements: 'With characteristic prostrating attacks occurring on average once a month over last several months',
        examples: 'Monthly severe migraines requiring bed rest, unable to work during attack, lasting 24+ hours',
        monthlyCompensation: 524
      },
      {
        rating: 50,
        requirements: 'With very frequent completely prostrating and prolonged attacks productive of severe economic inadaptability',
        examples: 'Weekly or more frequent severe migraines, often requiring ER visits, missing significant work time',
        monthlyCompensation: 1075
      }
    ],
    evidenceRequired: [
      'Headache diary documenting frequency, duration, and severity',
      'VA or private neurology examination',
      'Treatment records (medications, ER visits for severe migraines)',
      'Statement describing how headaches affect work and daily activities',
      'Service medical records documenting headaches',
      'Employer statements about missed work due to migraines'
    ],
    diagnosticCode: '8100',
    alt: 'Migraine headaches neurological condition information card'
  }
];

export default function DatabaseInteractive() {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedConditionId, setExpandedConditionId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    bodySystem: 'all',
    rating: 'all'
  });

  const bodySystems = ['all', 'Mental Health', 'Hearing', 'Neurological', 'Musculoskeletal', 'Cardiovascular', 'Respiratory', 'Gastrointestinal', 'Endocrine', 'Dermatological', 'Genitourinary', 'Ophthalmological', 'Oral', 'Other'];
  const ratingOptions = ['all', '10', '30', '50', '70', '100'];

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleToggleExpand = (conditionId: string) => {
    setExpandedConditionId(expandedConditionId === conditionId ? null : conditionId);
  };

  const filteredConditions = conditions.filter(condition => {
    const matchesSearch = condition.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         condition.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         condition.plainLanguageExplanation.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:208:4" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="208" data-component-end-line="288" data-component-file="DatabaseInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
      {/* Search and Filters Section */}
      <div data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:210:6" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="210" data-component-end-line="252" data-component-file="DatabaseInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20shadow-subtle%20p-6%20border%20border-border%22%7D" className="bg-card rounded-lg shadow-subtle p-6 border border-border">
        <div data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:211:8" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="211" data-component-end-line="213" data-component-file="DatabaseInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
          <SearchBar data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:212:10" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="212" data-component-end-line="212" data-component-file="DatabaseInteractive.tsx" data-component-name="SearchBar" data-component-content="%7B%22elementName%22%3A%22SearchBar%22%7D" onSearch={handleSearch} placeholder="Search conditions, symptoms, or keywords..." />
        </div>

        <div data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:215:8" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="215" data-component-end-line="251" data-component-file="DatabaseInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-4%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Body System Filter */}
          <div data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:217:10" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="217" data-component-end-line="232" data-component-file="DatabaseInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <label data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:218:12" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="218" data-component-end-line="220" data-component-file="DatabaseInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Body%20System%22%7D" className="block text-sm font-medium text-text-primary mb-2">
              Body System
            </label>
            <select data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:221:12" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="221" data-component-end-line="231" data-component-file="DatabaseInteractive.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22className%22%3A%22w-full%20px-4%20py-2%20border%20border-border%20rounded-lg%20bg-background%20text-text-primary%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%22%7D"
              value={filters.bodySystem}
              onChange={(e) => handleFilterChange('bodySystem', e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {bodySystems.map(system => (
                <option data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:227:16" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="227" data-component-end-line="229" data-component-file="DatabaseInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22%5Bvar%3Asystem%5D%22%7D" key={system} value={system}>
                  {system === 'all' ? 'All Body Systems' : system}
                </option>
              ))}
            </select>
          </div>

          {/* Rating Range Filter */}
          <div data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:235:10" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="235" data-component-end-line="250" data-component-file="DatabaseInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <label data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:236:12" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="236" data-component-end-line="238" data-component-file="DatabaseInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Rating%20Range%22%7D" className="block text-sm font-medium text-text-primary mb-2">
              Rating Range
            </label>
            <select data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:239:12" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="239" data-component-end-line="249" data-component-file="DatabaseInteractive.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22className%22%3A%22w-full%20px-4%20py-2%20border%20border-border%20rounded-lg%20bg-background%20text-text-primary%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%22%7D"
              value={filters.rating}
              onChange={(e) => handleFilterChange('rating', e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {ratingOptions.map(option => (
                <option data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:245:16" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="245" data-component-end-line="247" data-component-file="DatabaseInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22%5Bvar%3Aoption%5D%22%7D" key={option} value={option}>
                  {option === 'all' ? 'All Ratings' : `${option}%`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:255:6" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="255" data-component-end-line="287" data-component-file="DatabaseInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <div data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:256:8" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="256" data-component-end-line="263" data-component-file="DatabaseInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-6%22%7D" className="flex items-center justify-between mb-6">
          <h2 data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:257:10" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="257" data-component-end-line="259" data-component-file="DatabaseInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-headline%20font-bold%20text-2xl%20text-primary%22%2C%22textContent%22%3A%22Search%20Results%22%7D" className="font-headline font-bold text-2xl text-primary">
            Search Results
          </h2>
          <span data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:260:10" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="260" data-component-end-line="262" data-component-file="DatabaseInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-text-secondary%22%2C%22textContent%22%3A%22Showing%20of%20conditions%22%7D" className="text-sm text-text-secondary">
            Showing {filteredConditions.length} of {conditions.length} conditions
          </span>
        </div>

        {filteredConditions.length === 0 ? (
          <div data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:266:10" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="266" data-component-end-line="274" data-component-file="DatabaseInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-muted%20rounded-lg%20p-12%20text-center%22%7D" className="bg-muted rounded-lg p-12 text-center">
            <Icon data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:267:12" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="267" data-component-end-line="267" data-component-file="DatabaseInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22text-text-secondary%20mx-auto%20mb-4%22%7D" name="MagnifyingGlassIcon" size={48} className="text-text-secondary mx-auto mb-4" />
            <h3 data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:268:12" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="268" data-component-end-line="270" data-component-file="DatabaseInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-cta%20font-semibold%20text-lg%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22No%20conditions%20found%22%7D" className="font-cta font-semibold text-lg text-text-primary mb-2">
              No conditions found
            </h3>
            <p data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:271:12" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="271" data-component-end-line="273" data-component-file="DatabaseInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Try%20adjusting%20your%20search%20or%20filter%20criteria%22%7D" className="text-text-secondary">
              Try adjusting your search or filter criteria
            </p>
          </div>
        ) : (
          <div data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:276:10" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="276" data-component-end-line="285" data-component-file="DatabaseInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
            {filteredConditions.map(condition => (
              <ConditionCard data-component-id="src/app/va-disability-database/components/DatabaseInteractive.tsx:278:14" data-component-path="src/app/va-disability-database/components/DatabaseInteractive.tsx" data-component-line="278" data-component-end-line="283" data-component-file="DatabaseInteractive.tsx" data-component-name="ConditionCard" data-component-content="%7B%22elementName%22%3A%22ConditionCard%22%7D"
                key={condition.id}
                condition={condition}
                isExpanded={expandedConditionId === condition.id}
                onToggleExpand={handleToggleExpand}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}